import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { debounce, Logger } from '@saviynt/common';
import { Box, Icon, InlineMessage, MenuInput } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { MOCK_USER_CONSULT_MODAL_USER_LIST } from '../../../tests/actions/consult/mockdata';
import { getUsersListWithSearch } from '../../redux/certdetails/action';

import { isEmptyObject, returnModifiedOptions } from './utils';

import './CertsUserListDropdown.css';

function CertsUserListDropdown({
  onSelect,
  defaultReassignee,
  defaultAssigneName,
  className,
  isDisabled,
  certKey,
  placeholder,
  showDefaultCertifier,
  selectedUser,
  certifierUserKey,
  inputFieldTestId,
  isCritical,
}) {
  const [usersList, setUsersList] = useState(
    inputFieldTestId ? MOCK_USER_CONSULT_MODAL_USER_LIST : []
  );
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMoreOptions, setHasMoreOptions] = useState(true);
  const [searchTerm, setSearchTerm] = useState(null);
  const LOAD_INCREMENT = 10;

  const dispatch = useDispatch();

  const getUsersList = async (searchText, offSet, maxCount) => {
    // TODO: Would like to not NEED this to stop api calls in tests errors, get with dev team to come up with a fix.
    // Disable for testing
    if (inputFieldTestId) return;

    const body = {
      certkey: certKey,
      offset: offSet,
      max: maxCount,
    };

    if (searchText) {
      // to search user by name
      body.sSearch = searchText;
    }

    if (!isLoading) {
      setIsLoading(true);
    }

    const list = [];
    let fetchedRecords = [];

    if (
      defaultReassignee !== '' &&
      defaultAssigneName !== '' &&
      showDefaultCertifier &&
      (searchText === '' || !searchText)
    ) {
      if (certifierUserKey !== defaultReassignee) {
        const defaultCertifier = {};

        defaultCertifier.value = defaultReassignee;
        defaultCertifier.label = `${defaultAssigneName} - default certifier`;
        defaultCertifier.isChecked = false;
        list.push(defaultCertifier);
      }
    }

    dispatch(getUsersListWithSearch(body))
      .then((data) => {
        if (offSet === 0 && !data?.resultsList) {
          setUsersList([]);
          setHasMoreOptions(false);
        } else if (offSet === 0 && data?.resultsList?.length) {
          fetchedRecords = returnModifiedOptions(data.resultsList);
          fetchedRecords = list.concat(fetchedRecords);
          setUsersList(fetchedRecords);
          setHasMoreOptions(true);
        } else if (offSet !== 0 && !data?.resultsList) {
          setHasMoreOptions(false);
        } else if (offSet !== 0 && data.resultsList?.length) {
          fetchedRecords = returnModifiedOptions(data.resultsList);
          setUsersList((prevList) => prevList.concat(fetchedRecords));
          setHasMoreOptions(true);
        }
      })
      .catch((error) => {
        Logger.error('getUsersListWithSearch error ', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    // initial load
    getUsersList(searchTerm, offset, LOAD_INCREMENT);
  }, []);

  const loadUsersList = useCallback((searchTermValue, offsetValue = 0) => {
    getUsersList(searchTermValue, offsetValue, LOAD_INCREMENT);
  }, []);

  // Wrap loadUsersList with debounce
  const debouncedLoadUsersList = useCallback(debounce(loadUsersList, 500), [
    loadUsersList,
  ]);

  const handleChange = (selectedOption) => {
    setUsersList((prevList) =>
      prevList.map((option) => {
        // eslint-disable-next-line no-param-reassign
        option.isChecked = option.value === selectedOption.value;

        return option;
      })
    );

    // on clear resetting the offset  to 0 and calling user api with out search
    if (selectedOption && isEmptyObject(selectedOption)) {
      setOffset(0);
      loadUsersList(null, 0);
    }

    onSelect(selectedOption);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (
      scrollHeight - scrollTop === clientHeight &&
      hasMoreOptions &&
      !isLoading
    ) {
      // load more options
      setIsLoading(true);
      setOffset((prevOffset) => {
        const newOffset = prevOffset + LOAD_INCREMENT;

        debouncedLoadUsersList(searchTerm, newOffset);

        return newOffset;
      });
    }
  };

  return (
    <Box
      className={`CertsUserListDropdown anyWidthPositionRelative ${className} ${
        selectedUser && 'Certs-userSelected'
      }`}>
      <MenuInput
        className={
          isDisabled
            ? 'CertsUserListDropdown--disableMenu'
            : 'CertsUserListDropdown--enableMenu'
        }
        options={usersList}
        onChange={handleChange}
        placeholder={placeholder}
        setSearchTerm={setSearchTerm}
        isLoading={isLoading}
        isCritical={isCritical}
        CriticalHelperText={
          !searchTerm || searchTerm === '' ? (
            <InlineMessage kind='missingRequiredField' size='small' />
          ) : (
            <InlineMessage
              text='Please select the correct option from the list.'
              colorTheme='critical'
              size='small'
              leftIcon={
                <Icon kind='AlertCriticalFilled' color='critical-700' />
              }
            />
          )
        }
        onScroll={handleScroll}
        dropdownClassName='Certs-dropdownSelect'
        hasMoreOptions={hasMoreOptions}
        suffixIcon={<Icon kind='Check' color='navy-700' size='medium' />}
        isSearchDisabled
        loadingText='Loading more results'
        onSearchTermChange={(value) => {
          setOffset(0);
          setSearchTerm(value);
          debouncedLoadUsersList(value.trim());
        }}
        heightConfigs={{ maxHeight: '11.5rem' }}
        menuItemHeightConfigs={{ height: '2rem' }}
        inputFieldTestId={inputFieldTestId}
      />
    </Box>
  );
}

CertsUserListDropdown.propTypes = {
  onSelect: PropTypes.func.isRequired,
  defaultReassignee: PropTypes.string.isRequired,
  defaultAssigneName: PropTypes.string.isRequired,
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
  certKey: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  selectedUser: PropTypes.shape({}).isRequired,
  showDefaultCertifier: PropTypes.bool,
  certifierUserKey: PropTypes.string.isRequired,
  inputFieldTestId: PropTypes.string,
  isCritical: PropTypes.bool,
};

CertsUserListDropdown.defaultProps = {
  placeholder: 'Select a known certifier',
  showDefaultCertifier: false,
  isCritical: false,
  className: '',
  isDisabled: false,
  inputFieldTestId: '',
};

export default CertsUserListDropdown;
